import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { palette, radius } from "@ux/tokens";

export type IconButtonTone = "neutral" | "danger";

export interface IconButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style" | "children"> {
    readonly icon: ReactNode;
    readonly label: string;
    readonly tone?: IconButtonTone;
}

export function IconButton({
    icon,
    label,
    tone = "neutral",
    disabled,
    ...rest
}: IconButtonProps): ReactElement {
    const [hover, setHover] = useState(false);

    const baseColor = tone === "danger" ? palette.danger : palette.fg;
    const hoverBg = tone === "danger" ? palette.dangerBg : palette.surfaceRaised;

    const style: CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: radius.sm,
        background: hover && disabled !== true ? hoverBg : "transparent",
        border: `1px solid ${palette.border}`,
        color: baseColor,
        cursor: disabled === true ? "not-allowed" : "pointer",
        opacity: disabled === true ? 0.55 : 1,
        padding: 0,
        transition: "background 120ms ease",
    };

    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            {...rest}
            disabled={disabled}
            style={style}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {icon}
        </button>
    );
}
